import { useCallback, useEffect, useState } from 'react';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';
import { EnvironmentAdminConfig } from './adminConfig.types';

export const ADMIN_CONFIG_PATH = '/channel/admin-config';

const isAdminConfig = (value: unknown): value is EnvironmentAdminConfig =>
  typeof value === 'object' && value !== null && Array.isArray((value as EnvironmentAdminConfig).tabs);

/**
 * Fetch the Edit Environment admin config while the panel is open. There is deliberately no
 * bundled fallback here: the backend already falls back to the copy shipped with the release,
 * and a second copy in the frontend could drift from the one saves are validated against.
 */
export const useEnvironmentAdminConfig = (enabled: boolean) => {
  const [config, setConfig] = useState<EnvironmentAdminConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ServiceWrapper.doGet(
        AppSettings.GMS_BACKEND_API_URL + ADMIN_CONFIG_PATH,
        null,
        null,
        AuthManager.getGmsToken(),
      );
      const data = response?.data?.data;
      if (!response?.errorfound && response?.data?.done !== false && isAdminConfig(data)) {
        setConfig(data);
      } else {
        setError(response?.data?.message || response?.message || 'Failed to load the environment settings configuration');
      }
    } catch {
      setError('Failed to load the environment settings configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled && !config) void load();
  }, [enabled, config, load]);

  return { config, loading, error, reload: load };
};
